﻿import React, {
    useContext,
    createContext,
    useState,
    MouseEventHandler,
    useEffect
} from "react";

import { AuthCodeMSALBrowserAuthenticationProvider } from "@microsoft/microsoft-graph-client/authProviders/authCodeMsalBrowser";
import { AccountInfo, InteractionType, PublicClientApplication } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import config from "./config";
import { getPhoto, getUser } from "./GraphService";
import { User } from "microsoft-graph";

export interface AppError {
    message: string,
    debug?: string;
};

type AppContext = {
    user?: User;
    account?: AccountInfo,
    error?: AppError;
    signIn?: MouseEventHandler<HTMLElement>;
    signOut?: MouseEventHandler<HTMLElement>;
    displayError?: Function;
    clearError?: Function;
    authProvider?: AuthCodeMSALBrowserAuthenticationProvider;
    photoUrl?: string
}

const appContext = createContext<AppContext>({
    user: undefined,
    account: undefined,
    error: undefined,
    signIn: undefined,
    signOut: undefined,
    displayError: undefined,
    clearError: undefined,
    authProvider: undefined,
    photoUrl: undefined
});

export function useAppContext(): AppContext {
    return useContext(appContext);
}

interface IProvideAppContextProps {
    children: React.ReactNode;
}

export default function ProvideAppContext({ children }: IProvideAppContextProps) {
    const auth = useProvideAppContext();
    return (
        <appContext.Provider value={auth}>
            {children}
        </appContext.Provider>
    );
}

function useProvideAppContext(): AppContext {

    const msal = useMsal();
    const [user, setUser] = useState<User | undefined>(undefined);
    const [error, setError] = useState<AppError | undefined>(undefined);
    let initialState = msal.instance.getActiveAccount()!;
    //console.log("active account: ", initialState);
    const [account, setAccount] = useState<AccountInfo | undefined>(initialState);
    const [photoUrl, setPhotoUrl] = useState<string | undefined>(undefined);


    const displayError = (message: string, debug?: string) => {
        setError({ message, debug });
    }

    const clearError = () => {
        setError(undefined);
    }

    // Used by the Graph SDK to authenticate API calls
    const authProvider = new AuthCodeMSALBrowserAuthenticationProvider(
        msal.instance as PublicClientApplication,
        {
            account: msal.instance.getActiveAccount()!,
            scopes: config.scopes,
            interactionType: InteractionType.Popup
        }
    );
    useEffect(() => {
        const checkUser = async () => {
            if (!user) {
                try {
                    // Check if user is already signed in
                    const activeAccount = msal.instance.getActiveAccount();
                    if (activeAccount) {

                        // Get the user from Microsoft Graph
                        const profile = await getUser(authProvider);
                        const photo = await getPhoto(authProvider);

                        setUser(profile);
                        setAccount(activeAccount);
                        setPhotoUrl(photo);
                        //console.log("setting active account", activeAccount);

                    } else {
                        //console.log("no account in checkUser()");
                    }
                } catch (err) {
                    console.log(err);
                    //displayError(err.message);
                }
            }
        };
        checkUser();
    });

    const signIn = async () => {
        var foo = await msal.instance.loginPopup({
            scopes: config.scopes,
            prompt: "select_account"
        });
        console.log("foo", foo);
        
        try {
            // Get the user from Microsoft Graph
            const profile = await getUser(authProvider);
            const photo = await getPhoto(authProvider);
            setUser(profile);
            if (foo.account) {
                setAccount(foo.account);
            }
            setPhotoUrl(photo);
        } catch (e) {
            console.log("Can't get user photo", e);
        }
    };

    const signOut = async () => {
        await msal.instance.logoutPopup();
        setUser(undefined);
        setAccount(undefined);
        setPhotoUrl(undefined);
        window.location.reload();
    };



    return {
        user,
        account,
        error,
        signIn,
        signOut,
        displayError,
        clearError,
        authProvider,
        photoUrl
    };
}